/**
 * Push Notification Helper for Custom Web Push Integration
 * Handles browser push notifications and backend subscription
 */

const PUSH_SUBSCRIPTION_KEY = 'push_subscribed';

/**
 * Convert VAPID public key from base64url to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Extract subscription details with properly encoded keys
 */
function getSubscriptionDetails(subscription: PushSubscription) {
  const p256dhKey = subscription.getKey('p256dh');
  const authKey = subscription.getKey('auth');
  
  if (!p256dhKey || !authKey) {
    throw new Error('Failed to get subscription keys');
  }
  
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(p256dhKey),
      auth: arrayBufferToBase64(authKey),
    },
  };
}

/**
 * Check if push notifications are supported in this browser
 */
export function isPushNotificationSupported(): boolean {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Get the current push subscription from the service worker
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) {
    console.log('📱 Push notifications not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('❌ Error getting push subscription:', error);
    return null;
  }
}

/**
 * Subscribe to push notifications and register with backend
 */
export async function subscribeToWebPush(
  userId: string,
  userEmail: string
): Promise<boolean> {
  if (!isPushNotificationSupported()) {
    console.warn('⚠️ Push notifications not supported on this device');
    return false;
  }

  try {
    const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';

    // Step 1: Get or create push subscription
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log('📱 Creating new push subscription...');
      
      // Get VAPID public key from environment
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not configured');
      }
      
      // Convert VAPID key to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      
      // Subscribe with VAPID key
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey,
      });
      
      console.log('✅ Push subscription created with VAPID key');
    } else {
      console.log('✅ Using existing push subscription');
    }

    // Step 2: Extract subscription details with properly encoded keys
    const subscriptionDetails = getSubscriptionDetails(subscription);
    console.log('📡 Registering push subscription with backend...');
    
    const registerResponse = await fetch(`${serverUrl}/api/notifications/register-push-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        userEmail,
        subscription: subscriptionDetails,
      }),
    });

    if (!registerResponse.ok) {
      throw new Error('Failed to register push subscription with backend');
    }
    
    console.log('✅ Push subscription registered with backend');
    console.log('📝 Subscription endpoint:', subscription.endpoint);

    // Store subscription state
    localStorage.setItem(PUSH_SUBSCRIPTION_KEY, 'true');
    localStorage.setItem('push_user_id', userId);
    localStorage.setItem('push_subscription_date', new Date().toISOString());

    return true;
  } catch (error) {
    console.error('❌ Error subscribing to push notifications:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const subscription = await getPushSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      localStorage.removeItem(PUSH_SUBSCRIPTION_KEY);
      localStorage.removeItem('push_user_id');
      localStorage.removeItem('push_subscription_date');
      console.log('✅ Unsubscribed from push notifications');
      return true;
    }
    return false;
  } catch (error) {
    console.error('❌ Error unsubscribing from push:', error);
    return false;
  }
}

/**
 * Check if user is already subscribed to push notifications
 */
export function isPushSubscribed(): boolean {
  return localStorage.getItem(PUSH_SUBSCRIPTION_KEY) === 'true';
}

/**
 * Get subscription info
 */
export function getSubscriptionInfo(): {
  isSubscribed: boolean;
  userId: string | null;
  subscriptionDate: string | null;
} {
  return {
    isSubscribed: isPushSubscribed(),
    userId: localStorage.getItem('push_user_id'),
    subscriptionDate: localStorage.getItem('push_subscription_date'),
  };
}

